/**
 * ChatInput Component
 * Text input with send button for the AI Chat panel. While an answer is streaming the
 * send button becomes Stop. The textarea clears only once `onSend` reports the
 * question was accepted, so a failed send keeps the text for a retry.
 */

import React, { useState, useRef, useEffect } from 'react';

interface ChatInputProps {
  /** Resolves true when the question was accepted (the input clears), false otherwise. */
  onSend: (message: string) => Promise<boolean> | boolean | void;
  isLoading: boolean;
  disabled?: boolean;
  /** When given, shows a Stop button while loading. */
  onStop?: () => void;
}

const MAX_LENGTH = 2000;

export const ChatInput: React.FC<ChatInputProps> = ({ onSend, isLoading, disabled, onStop }) => {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [value]);

  const handleSubmit = async () => {
    const trimmed = value.trim();
    if (!trimmed || isLoading || disabled) return;
    const accepted = await onSend(trimmed);
    if (accepted === false) return; // keep the text for a retry
    setValue('');
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  return (
    <div className="border-t border-gray-200 bg-white p-3">
      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value.substring(0, MAX_LENGTH))}
          onKeyDown={handleKeyDown}
          placeholder="Ask about your data..."
          aria-label="Ask about your data"
          rows={1}
          disabled={isLoading || disabled}
          className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
            disabled:bg-gray-50 disabled:text-gray-400
            placeholder:text-gray-400"
        />
        {isLoading && onStop ? (
          <button
            onClick={onStop}
            className="flex-shrink-0 rounded-lg bg-gray-700 p-2 text-white hover:bg-gray-800 transition-colors"
            title="Stop"
            aria-label="Stop"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="6" width="12" height="12" rx="2" />
            </svg>
          </button>
        ) : (
          <button
            onClick={() => { void handleSubmit(); }}
            disabled={!value.trim() || isLoading || disabled}
            className="flex-shrink-0 rounded-lg bg-blue-600 p-2 text-white
              hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed
              transition-colors"
            title="Send message"
            aria-label="Send message"
          >
            {isLoading ? (
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
              </svg>
            )}
          </button>
        )}
      </div>
      {value.length > MAX_LENGTH * 0.9 && (
        <p className="text-xs text-gray-400 mt-1 text-right">
          {value.length}/{MAX_LENGTH}
        </p>
      )}
    </div>
  );
};
